import * as yup from "yup";
import { Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { Button } from "../../../../components/ui/button";
import "../../../../assets/nascent.css";
import Common from "../../../../helper/common";
import {
  FormikInput,
  FormikSearchSelect,
  FormikSelect,
  PhoneInputField,
} from "../../../../components/formik-components";
import { respEnum } from "../../../../constants/appConstant";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../../components/ui/dialog";
import { Checkbox } from "../../../../components/ui/checkbox";
import { Label } from "../../../../components/ui/label";
import Card from "../../../../components/ui/card-snippet";

const FormikInitialValues = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
  phoneCode: "+92",
  phone: "",
  phoneNum: "",
  password: "",
  masterTournamentId: null,
  courtIds: [],
};

const formikValidation = (id) =>
  yup.object({
    firstName: yup
      .string()
      .trim()
      .min(2)
      .max(50)
      .required("First Name is Required"),
    lastName: yup
      .string()
      .trim()
      .min(2)
      .max(50)
      .required("Last Name is Required"),
    phone: yup.string().trim().required("Phone Number is required"),
    phoneNumber: yup
      .string()
      .trim()
      .when("phone", {
        is: (val) => !val || val === "",
        then: (schema) => schema.required("Phone Number is required"),
        otherwise: (schema) => schema,
      }),
    phoneCode: yup.string().when("phone", {
      is: (val) => !val || val === "",
      then: (schema) => schema.required("Phone Code is required"),
      otherwise: (schema) => schema,
    }),
    password: yup
      .string()
      .nullable()
      .when([], {
        is: () => !id || id === 0,
        then: (schema) =>
          schema
            .min(6, "Password must be at least 6 characters")
            .required("Password is required"),
        otherwise: (schema) =>
          schema
            .nullable()
            .transform((value) => {
              // Convert empty string to null in edit mode
              if (value === "" || value === null || value === undefined) {
                return null;
              }
              return value;
            })
            .test(
              "min-length",
              "Password must be at least 6 characters",
              function (value) {
                // In edit mode, password is optional - allow null/empty
                if (!value || value.trim() === "") {
                  return true; // Valid - optional field
                }
                // If provided, must be at least 6 characters
                return value.length >= 6;
              }
            ), // Optional in edit mode - can be null/empty, but if provided must be 6+ chars
      }),
    masterTournamentId: yup
      .number()
      .nullable()
      .required("Master Tournament is required"),
  });

const AddTournamentUmpire = ({ id, onClose, open, setOpen }) => {
  const formikRef = useRef();
  const [masterTournaments, setMasterTournaments] = useState([]);
  const [availableCourts, setAvailableCourts] = useState([]);
  const [selectedCourts, setSelectedCourts] = useState([]);
  const [isFormikInitialized, setIsFormikInitialized] = useState(false);
  const [loadingCourts, setLoadingCourts] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      await GetMasterTournaments();
    };
    fetchData();
  }, []);

  useEffect(() => {
    setIsFormikInitialized(open);
  }, [open]);

  useEffect(() => {
    if (id) {
      getDataInfo();
    }
  }, [id]);

  useEffect(() => {
    if (
      formikRef.current?.values?.masterTournamentId &&
      formikRef.current.values.masterTournamentId !== null
    ) {
      loadCourts(formikRef.current.values.masterTournamentId);
    }
  }, [formikRef.current?.values?.masterTournamentId]);

  const GetMasterTournaments = async () => {
    const response = await Common.ApiService.getInstance().request(
      "GetMasterTournaments"
    );
    const data = [
      { id: null, name: "Select Master Tournament" },
      ...(response?.data ?? []),
    ];
    setMasterTournaments(data);
  };

  const loadCourts = async (masterTournamentId) => {
    if (!masterTournamentId) return;

    setLoadingCourts(true);
    try {
      const response = await Common.ApiService.getInstance().request(
        `GetMasterTournamentCourtsForUmpire?masterTournamentId=${masterTournamentId}`
      );

      if (response.status === respEnum.Success && response.data) {
        // Response data is already grouped by club
        let groupedCourts = Array.isArray(response.data) ? response.data : [];

        // If editing, mark courts as selected if they're in courtIds
        if (id && formikRef.current?.values?.courtIds) {
          const selectedCourtIds = formikRef.current.values.courtIds || [];
          groupedCourts.forEach((club) => {
            if (club.courts && Array.isArray(club.courts)) {
              club.courts.forEach((court) => {
                court.isSelected = selectedCourtIds.includes(court.courtId);
              });
            }
          });
        }

        // Get all selected court IDs for tracking
        const allSelectedCourts = groupedCourts
          .flatMap((club) => club.courts || [])
          .filter((c) => c.isSelected)
          .map((c) => c.courtId);

        setAvailableCourts(groupedCourts);
        setSelectedCourts(allSelectedCourts);
      }
    } catch (error) {
      Common.SweetAlert.alert("Error loading courts", "error");
    } finally {
      setLoadingCourts(false);
    }
  };

  const handleCourtToggle = (courtId) => {
    const newSelected = selectedCourts.includes(courtId)
      ? selectedCourts.filter((id) => id !== courtId)
      : [...selectedCourts, courtId];

    setSelectedCourts(newSelected);
    if (formikRef.current) {
      formikRef.current.setFieldValue("courtIds", newSelected);
    }

    // Update availableCourts to reflect selection
    setAvailableCourts((prev) =>
      prev.map((club) => ({
        ...club,
        courts: club.courts.map((court) => ({
          ...court,
          isSelected: newSelected.includes(court.courtId),
        })),
      }))
    );
  };

  const getDataInfo = async () => {
    const response = await Common.ApiService.getInstance().request(
      `GetTournamentUmpireDetails?id=${id}`
    );
    if (response.status === respEnum.Success && response.data.length > 0) {
      const d = response?.data?.[0];

      // Construct full phone number with country code for PhoneInputField
      const fullPhone =
        d.phoneCode && d.phoneNumber
          ? `${d.phoneCode}${d.phoneNumber}`
          : d.phoneNumber || "";

      formikRef.current.setValues({
        ...d,
        phone: fullPhone,
        phoneCode: d.phoneCode || "+92",
        phoneNum: d.phoneNumber || "",
        courtIds: d.courtIds || [],
      });

      // Load courts if masterTournamentId exists
      if (d.masterTournamentId) {
        await loadCourts(d.masterTournamentId);
      }
    }
  };

  const onSubmitData = async (d, action) => {
    if (!d.masterTournamentId) {
      Common.SweetAlert.alert("Please select a Master Tournament", "error");
      action.setSubmitting(false);
      return;
    }

    if (!d.courtIds || d.courtIds.length === 0) {
      Common.SweetAlert.alert(
        "Please select at least one court for access",
        "error"
      );
      action.setSubmitting(false);
      return;
    }

    // Extract phoneNumber from phone or use existing phoneNumber
    let phoneNumber = d.phoneNumber;
    let phoneCode = d.phoneCode;

    // If phone field is filled (from PhoneInputField), extract phoneNumber
    if (d.phone) {
      phoneCode = d.phoneCode || "+92";
      phoneNumber = d.phoneNum || d.phone.replace(phoneCode, "").trim();
    }

    // Prepare data object
    let data = {
      ...d,
      phoneNumber: phoneNumber,
      phoneCode: phoneCode,
      id: id || 0,
    };

    // In edit mode, if password is empty/null, don't send it at all
    // This prevents "password cannot be null" error
    if (id && (!d.password || d.password.trim() === "")) {
      delete data.password;
    }

    let response = await Common.ApiService.getInstance().request(
      "AddUpdateTournamentUmpire",
      data,
      "POST"
    );
    action.setSubmitting(false);
    if (response.status === respEnum.Success) {
      Common.SweetAlert.alert(response.message, "success");
      onClose(response.data);
      setOpen(false);
    }
  };

  return (
    <div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            size="sm"
            variant="primary"
            className="flex items-center gap-2"
          >
            <Icon icon="heroicons:plus-20-solid" className="h-4 w-4" />
            Add
          </Button>
        </DialogTrigger>
        <DialogContent className="custom-modal max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="border-b pb-4 mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 -mx-6 px-6 py-4 rounded-t-lg">
            <DialogTitle className="text-2xl font-bold flex items-center gap-2 text-gray-800">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Icon
                  icon={id ? "heroicons:pencil-square" : "heroicons:user-plus"}
                  className="h-6 w-6 text-primary"
                />
              </div>
              {id ? "Update" : "Add"} Tournament Umpire
            </DialogTitle>
          </DialogHeader>

          <div className="px-1">
            <Formik
              innerRef={formikRef}
              onSubmit={onSubmitData}
              initialValues={FormikInitialValues}
              validationSchema={formikValidation(id)}
              validateOnChange={false}
              validateOnBlur={false}
            >
              {(formikProps) => (
                <div className="space-y-8">
                  {/* Personal Information Section */}
                  <div className="space-y-4 p-5 bg-gradient-to-br from-blue-50/50 to-purple-50/50 rounded-xl border border-blue-100">
                    <div className="flex items-center gap-2 pb-3 border-b border-blue-200">
                      <div className="p-2 bg-blue-500 rounded-lg">
                        <Icon
                          icon="heroicons:user"
                          className="h-5 w-5 text-white"
                        />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800">
                        Personal Information
                      </h3>
                    </div>
                    <div className="md:grid md:grid-cols-2 gap-4 max-w-3xl">
                      <FormikInput
                        formikProps={formikProps}
                        name={"firstName"}
                        label={"First Name"}
                        placeholder={"Enter first name"}
                        maxLength="50"
                      />

                      <FormikInput
                        formikProps={formikProps}
                        name={"lastName"}
                        label={"Last Name"}
                        placeholder={"Enter last name"}
                        maxLength="50"
                      />

                      <div className="flex flex-col gap-2">
                        <PhoneInputField formikProps={formikProps} />
                      </div>

                      <FormikSearchSelect
                        formikProps={formikProps}
                        name={"masterTournamentId"}
                        data={masterTournaments}
                        placeholder={"Select Master Tournament"}
                        label={"Master Tournament"}
                        onChange={(value) => {
                          formikProps.setFieldValue(
                            "masterTournamentId",
                            value
                          );
                          formikProps.setFieldValue("courtIds", []);
                          setSelectedCourts([]);
                          setAvailableCourts([]);
                        }}
                      />
                    </div>
                  </div>

                  {/* Security Section */}
                  <div className="space-y-4 p-5 bg-gradient-to-br from-green-50/50 to-emerald-50/50 rounded-xl border border-green-100">
                    <div className="flex items-center gap-2 pb-3 border-b border-green-200">
                      <div className="p-2 bg-green-500 rounded-lg">
                        <Icon
                          icon="heroicons:lock-closed"
                          className="h-5 w-5 text-white"
                        />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800">
                        Security
                      </h3>
                    </div>
                    <div className="md:grid md:grid-cols-2 gap-4 max-w-3xl">
                      {!id && (
                        <FormikInput
                          formikProps={formikProps}
                          name={"password"}
                          label={"Password"}
                          placeholder={"Enter password (min 6 characters)"}
                          type="password"
                          maxLength="100"
                        />
                      )}

                      {id && (
                        <FormikInput
                          formikProps={formikProps}
                          name={"password"}
                          label={"New Password"}
                          placeholder={"Leave empty to keep current password"}
                          type="password"
                          maxLength="100"
                        />
                      )}
                    </div>
                  </div>

                  {/* Court Selection Section */}
                  {formikProps.values.masterTournamentId && (
                    <div className="space-y-4 p-5 bg-gradient-to-br from-orange-50/50 to-amber-50/50 rounded-xl border border-orange-100">
                      <div className="flex items-center justify-between pb-3 border-b border-orange-200">
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-orange-500 rounded-lg">
                            <Icon
                              icon="heroicons:map-pin"
                              className="h-5 w-5 text-white"
                            />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-800">
                            Court Access
                          </h3>
                        </div>
                        {selectedCourts.length > 0 && (
                          <span className="text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-amber-500 px-4 py-1.5 rounded-full shadow-md">
                            <Icon
                              icon="heroicons:check-circle"
                              className="h-4 w-4 inline mr-1"
                            />
                            {selectedCourts.length}{" "}
                            {selectedCourts.length === 1 ? "Court" : "Courts"}{" "}
                            Selected
                          </span>
                        )}
                      </div>
                      {loadingCourts ? (
                        <div className="text-center py-12">
                          <Icon
                            icon="heroicons:arrow-path"
                            className="h-8 w-8 mx-auto mb-2 text-primary animate-spin"
                          />
                          <p className="text-gray-500">Loading courts...</p>
                        </div>
                      ) : availableCourts.length > 0 ? (
                        <div className="space-y-4">
                          {availableCourts.map((club) => (
                            <Card
                              key={club.clubId}
                              title={
                                <div className="flex items-center gap-2">
                                  <div className="p-1.5 bg-gradient-to-br from-blue-400 to-purple-400 rounded-lg">
                                    <Icon
                                      icon="heroicons:building-office"
                                      className="h-4 w-4 text-white"
                                    />
                                  </div>
                                  <span className="font-semibold text-gray-800">
                                    {club.clubName}
                                  </span>
                                </div>
                              }
                              className="mb-4 shadow-md border-2 border-gray-200 hover:border-orange-300 transition-all bg-white"
                            >
                              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 p-4">
                                {club.courts.map((court) => (
                                  <div
                                    key={court.courtId}
                                    className={`flex items-center space-x-2 p-3 rounded-lg transition-all cursor-pointer ${
                                      court.isSelected
                                        ? "bg-gradient-to-r from-orange-100 to-amber-100 border-2 border-orange-400 shadow-md"
                                        : "hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 border-2 border-transparent hover:border-gray-300"
                                    }`}
                                  >
                                    <Checkbox
                                      id={`court-${court.courtId}`}
                                      checked={court.isSelected}
                                      onCheckedChange={() =>
                                        handleCourtToggle(court.courtId)
                                      }
                                    />
                                    <Label
                                      htmlFor={`court-${court.courtId}`}
                                      className={`text-sm font-medium cursor-pointer flex-1 ${
                                        court.isSelected
                                          ? "text-primary"
                                          : "text-gray-700"
                                      }`}
                                    >
                                      {court.courtName}
                                    </Label>
                                  </div>
                                ))}
                              </div>
                            </Card>
                          ))}
                          {selectedCourts.length === 0 && (
                            <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-300 rounded-lg shadow-sm">
                              <div className="p-2 bg-yellow-400 rounded-full">
                                <Icon
                                  icon="heroicons:exclamation-triangle"
                                  className="h-5 w-5 text-yellow-900"
                                />
                              </div>
                              <p className="text-sm text-yellow-900 font-semibold">
                                Please select at least one court for access
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border-2 border-gray-300">
                          <div className="p-4 bg-gray-200 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                            <Icon
                              icon="heroicons:map-pin"
                              className="h-8 w-8 text-gray-500"
                            />
                          </div>
                          <p className="text-gray-600 font-semibold">
                            No courts available for this master tournament
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Formik>
          </div>
          <DialogFooter className="mt-8 gap-3 pt-6 border-t bg-gradient-to-r from-gray-50 to-gray-100 -mx-6 px-6 py-4 rounded-b-lg">
            <DialogClose asChild>
              <Button
                type="button"
                variant="outline"
                className="border-2 hover:bg-gray-100"
              >
                <Icon icon="heroicons:x-mark" className="h-4 w-4 mr-2" />
                Close
              </Button>
            </DialogClose>

            {isFormikInitialized && formikRef.current && (
              <Button
                type="button"
                variant="primary"
                className="bg-gradient-to-r from-primary to-blue-600 hover:from-primary/90 hover:to-blue-600/90 shadow-md"
                onClick={(e) => {
                  e.preventDefault();
                  formikRef.current?.handleSubmit();
                }}
              >
                {formikRef.current.isSubmitting ? (
                  <>
                    <Icon
                      icon="heroicons:arrow-path"
                      className="h-4 w-4 mr-2 animate-spin"
                    />
                    Saving...
                  </>
                ) : (
                  <>
                    <Icon
                      icon={!id ? "heroicons:plus" : "heroicons:check"}
                      className="h-4 w-4 mr-2"
                    />
                    {!id ? "Add" : "Update"}
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddTournamentUmpire;
